import React from "react";
import {
    Box,
    Button, Checkbox, FormControlLabel, FormGroup,
    IconButton,
    Stack, Switch,
    TextField, Tooltip,
    Typography
} from "@mui/material";
import {menuItemsInfo} from "../editMenuItems";
import {IconPlus, IconX} from "@tabler/icons-react";
import {NodeType} from "../../../../../model/ModelData";
import {v4 as uuidv4} from "uuid";
import {FlowSettings} from "./FlowSettings";
import {InputSettings} from "./InputSettings";
import {ConfirmationSettings} from "./ConfirmationSettings";
import {HookSettings} from "./HookSettings";
import {QASettings} from "./QASettings";
import {LLMSettings} from "./LLMSettings";
import {TeachingSettings} from "./TeachingSettings";
import {RecommendationSettings} from "./RecommendationSettings";
import {ChoiceSettings} from "./ChoiceSettings";
import {StartNodeSettings} from "./StartNodeSettings";
import {UserInputSettings} from "./components/user-input-settings";
import {WatchdogSelection} from "./WatchdogSelection";

export const SettingsTab = ({node, setNode, onUpdate, project, nodes}) => {
    const onChangeData = (key, value) => {
        setNode(prev => {
            const state = {...prev};
            state.data[key] = value;

            return state;
        })
    }

    const {primary, secondary} = menuItemsInfo.settings;

    return (
        <>
            <Box sx={{borderBottom: 1, borderColor: "divider", pb: 0.5}}>
                <Typography fontWeight={700} sx={{pb: 1}}>{primary}</Typography>
                <Typography variant={"body2"} fontSize={13}>{secondary}</Typography>
            </Box>
            <TextField
                value={node?.data?.max_ttl || ""}
                size={"small"}
                type={"number"}
                label={"Max TTL"}
                placeholder={"Enter number"}
                helperText={"Maximum attempts to understand user"}
                onChange={e => onChangeData("max_ttl", e.target.value)}
                onBlur={() => onUpdate(node)}
            />
            <Box sx={{px: 1}}>
                <FormGroup>
                    <LLMSettings node={node} setNode={setNode} onUpdate={onUpdate} onChangeData={onChangeData}
                                 project={project}
                                 nodes={nodes}/>
                    <FormControlLabel
                        size={"small"}
                        control={(
                            <Switch checked={!!node?.data?.data_protection}
                                    onChange={(event => onChangeData("data_protection", event.target.checked))}
                                    onBlur={() => onUpdate(node)}/>
                        )} label={`Enable Data Protection`}/>
                    <FormControlLabel
                        size={"small"}
                        control={(
                            <Switch checked={!!node?.data?.hint}
                                    onChange={(event => onChangeData("hint", event.target.checked))}
                                    onBlur={() => onUpdate(node)}/>
                        )} label={`Use Hint`}/>
                    {
                        node?.type === NodeType.QUERY &&
                        <FormControlLabel
                            size={"small"}
                            control={(
                                <Switch checked={!!node?.data?.global_intents_included}
                                        onChange={(event => onChangeData("global_intents_included", event.target.checked))}
                                        onBlur={() => onUpdate(node)}/>
                            )} label={`Include Global Intents`}/>
                    }
                    {/*All the input nodes have this Music on Hold switch*/}
                    {(node?.type === NodeType?.QUERY || node?.type === NodeType.TEXT || node?.type === NodeType.CHOICE) && (
                        <FormControlLabel
                            size={"small"}
                            control={(
                                <Switch checked={!!node?.data?.music_on_hold}
                                        onChange={(event => onChangeData("music_on_hold", event.target.checked))}
                                        onBlur={() => onUpdate(node)}/>
                            )} label={`Music on Hold`}/>
                    )}
                </FormGroup>
            </Box>
            {
                node?.type === NodeType?.CALL_TRANSFER && (
                    <>
                        <Box>
                            <Typography fontWeight={700} gutterBottom fontSize={13}>Transfer Number</Typography>
                            <TextField
                                value={node?.data?.transfer_number || ""}
                                size={"small"}
                                placeholder={"Enter the number to redirect the call"}
                                helperText={"Enter the phone number where the call should be transferred."}
                                onChange={e => onChangeData("transfer_number", e.target.value)}
                                onBlur={() => onUpdate(node)}
                            />
                        </Box>
                    </>
                )
            }
            {
                node?.type === NodeType?.START &&
                <StartNodeSettings node={node} setNode={setNode} onUpdate={onUpdate} onChangeData={onChangeData}
                                   project={project} nodes={nodes}/>
            }
            {
                node?.type === NodeType?.TEACHING &&
                <TeachingSettings node={node} setNode={setNode} onUpdate={onUpdate} onChangeData={onChangeData}
                                  project={project} nodes={nodes}/>
            }
            {
                node?.type === NodeType?.START_FLOW &&
                <FlowSettings node={node} setNode={setNode} onUpdate={onUpdate} onChangeData={onChangeData}
                              project={project}
                              nodes={nodes}/>
            }
            {
                node?.type === NodeType?.QA &&
                <QASettings node={node} setNode={setNode} onUpdate={onUpdate} onChangeData={onChangeData}
                            project={project}
                            nodes={nodes}/>
            }
            {
                node?.type === NodeType?.INPUT &&
                <InputSettings node={node} setNode={setNode} onUpdate={onUpdate} onChangeData={onChangeData}
                               project={project}
                               nodes={nodes}/>
            }
            {
                node?.type === NodeType?.RECOMMENDATION &&
                <RecommendationSettings node={node} setNode={setNode} onUpdate={onUpdate} onChangeData={onChangeData}
                                        project={project} nodes={nodes}/>
            }
            {
                node?.type === NodeType?.CHOICE &&
                <ChoiceSettings node={node} setNode={setNode} onUpdate={onUpdate} onChangeData={onChangeData}
                                project={project}
                                nodes={nodes}/>
            }
            {
                node?.type === NodeType?.QUERY &&
                <>
                    <UserInputSettings node={node} onUpdate={onUpdate} onChangeData={onChangeData}/>
                </>
            }
            {
                [NodeType.QUERY, NodeType.TEXT, NodeType.TEACHING, NodeType.RECOMMENDATION, NodeType.CHOICE].includes(node?.type) &&
                <>
                    <ConfirmationSettings node={node} setNode={setNode} project={project}
                                          onChangeData={onChangeData}
                                          nodes={nodes}
                                          onUpdate={onUpdate}/>
                    {/*<KnowledgeBaseSettings node={node} setNode={setNode} project={project} onChangeData={onChangeData}*/}
                    {/*                   onUpdate={onUpdate}/>*/}
                </>
            }
            {
                node?.type === NodeType?.HOOK &&
                <HookSettings node={node} setNode={setNode} onUpdate={onUpdate} onChangeData={onChangeData}
                              project={project}
                              nodes={nodes}/>
            }
            {
                [NodeType.QUERY, NodeType.INPUT, NodeType.GEN_AI_WORKFLOW, NodeType.RECOMMENDATION, NodeType.QA]
                    .includes(node?.type) &&
                <WatchdogSelection
                    node={node} setNode={setNode}
                    onUpdate={onUpdate} onChangeData={onChangeData}
                    project={project} nodes={nodes}
                />
            }
            <CustomParamData
                node={node} setNode={setNode} project={project}
                nodes={nodes}
                onChangeData={onChangeData}
                onUpdate={onUpdate}
            />
        </>
    )
}

const CustomParamData = ({node, setNode, project, onChangeData, onUpdate}) => {

    return (
        <>
            <Stack direction={"row"} gap={2} justifyContent={"space-between"} alignItems={"center"}>
                <Typography fontWeight={700} fontSize={13}>Custom Params</Typography>

                <Tooltip title={"Add Custom Param"}>
                    <IconButton
                        variant={"outlined"} color={"secondary"} size={"small"}
                        onClick={() => {
                            setNode(prev => {
                                const state = {...prev};
                                if (!state?.data?.custom_params) {
                                    state.data.custom_params = {}
                                }
                                const id = uuidv4();
                                state.data.custom_params[id] = "";
                                return state
                            })
                        }}
                    >
                        <IconPlus size={18}/>
                    </IconButton>
                </Tooltip>
            </Stack>
            {
                node?.data?.custom_params &&
                Object.keys(node?.data?.custom_params)?.sort()
                    ?.map((custom_param_key, index) => {
                        return (
                            <Stack
                                direction={"row"}
                                gap={1}
                                key={custom_param_key}
                            >
                                <TextField
                                    size={"small"}
                                    min={0}
                                    defaultValue={custom_param_key}
                                    InputLabelProps={{shrink: true}}
                                    sx={{flex: 1}}
                                    onBlur={(e) => {
                                        setNode(prev => {
                                            const state = {...prev};
                                            if (!state?.data?.custom_params) {
                                                state.data.custom_params = {}
                                            }
                                            const value = state.data.custom_params[custom_param_key] || "";
                                            delete state.data.custom_params[custom_param_key];
                                            state.data.custom_params[e.target.value] = value;
                                            onUpdate(state)
                                            return state
                                        })
                                    }}
                                />

                                <TextField
                                    size={"small"}
                                    min={0}
                                    value={node.data.custom_params[custom_param_key] || ""}
                                    InputLabelProps={{shrink: true}}
                                    sx={{flex: 1}}
                                    onChange={(e) => {
                                        setNode(prev => {
                                            const state = {...prev};
                                            if (!state?.data?.custom_params) {
                                                state.data.custom_params = {}
                                            }
                                            state.data.custom_params[custom_param_key] = e.target.value;
                                            return state
                                        })
                                    }}
                                    onBlur={() => onUpdate(node)}
                                />
                                <IconButton
                                    onClick={() => {
                                        setNode(prev => {
                                            const state = {...prev};
                                            delete state.data.custom_params[custom_param_key];
                                            return state
                                        })
                                    }}
                                >
                                    <IconX size={15}/>
                                </IconButton>
                            </Stack>
                        )
                    })
            }

        </>
    )
}